import React, { useState, useEffect } from "react";

import useAxiosPrivate from "../../../hooks/useAxiosPrivate.js";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import useUserProfile from "../../../hooks/useUserProfile.js";
import { Snippet, Tooltip } from "@heroui/react";
import useEntities from "@/hooks/useEntities";
import { useUseCases } from "@/context/UseCasesProvider";

import { BsBuildingsFill } from "react-icons/bs";
import { FaUserCircle } from "react-icons/fa";

type HeaderProps = {
  user: string;
  env?: boolean;
};

type UserData = {
  user_group: string;
  user_id: string;
  creation_time: string;
  user_email: string;
  user_given_name: string;
  customer_group_id_list: string;
  update_datetime: string;
  entities: string;
  user_name: string;
  account_status: boolean;
  availability: string;
  organization_name: string;
};

type EntityData = {
  entity_id: string;
  entity_name: string;
  entity_color: string;
  entity_initials: string;
  entity_address: string;
  entity_postalCode: string;
  entity_city: string;
  entity_email: string[];
  entity_email_ingest?: string[];
};

const Header = ({ user, env }: HeaderProps): React.ReactElement => {
  const { t, i18n } = useTranslation();
  const axiosPrivate = useAxiosPrivate();
  const { isAdminVistaflow } = useUserProfile();
  const { entities } = useEntities();
  const entitiesList = entities as EntityData[];
  const settingsInterface = useUseCases();

  const { data, isLoading } = useQuery({
    queryKey: ["user"],
    queryFn: async () => {
      const response = await axiosPrivate.get<UserData>(`/users/${user}`);
      return response.data;
    },
    staleTime: 86400000, // 1 day
    gcTime: 86400000, // 1 day
    refetchOnWindowFocus: false,
  });

  const queryClient = useQueryClient();
  const [availability, setAvailability] = useState<string>(data?.availability ?? "active");

  useEffect(() => {
    if (data?.availability) {
      setAvailability(data.availability);
    }
  }, [data?.availability]);

  const availabilityMutation = useMutation({
    mutationFn: async (newAvailability: string) => {
      await axiosPrivate.put(`/users/${user}`, { availability: newAvailability });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user"] });
    },
    onError: () => {
      setAvailability((prev) => (prev === "active" ? "inactive" : "active"));
    },
  });

  const changeLanguage = (lng: string): void => {
    i18n.changeLanguage(lng);
  };

  const getUserEntities = (): EntityData[] => {
    if (!data?.entities || !entitiesList) return [];

    const userEntityIds = data.entities.split(",").map((id) => id.trim());
    return entitiesList.filter((entity) => userEntityIds.includes(entity.entity_id));
  };

  if (isLoading || !data) {
    return <></>;
  }

  const userEntities = getUserEntities();
  return (
    <>
      <div className="flex items-center text-xl text-vistagrey/70">
        <BsBuildingsFill className="mx-2" />
        <Tooltip
          placement="bottom-start"
          content={
            <div className="px-1 py-2">
              {userEntities.length > 0 ? (
                <div className="space-y-2">
                  {userEntities.map((entity) => (
                    <div key={entity.entity_id} className="flex items-center gap-2">
                      <div
                        className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium text-white"
                        style={{ backgroundColor: entity.entity_color }}
                      >
                        {entity.entity_initials}
                      </div>
                      <Snippet
                        size="sm"
                        variant="flat"
                        hideSymbol
                        disableTooltip
                        classNames={{
                          base: "bg-white text-vistagrey/70",
                        }}
                      >
                        {(() => {
                          const emails = entity.entity_email_ingest || entity.entity_email || [];
                          if (emails.length === 0) return "N/A";

                          if (env) {
                            const nonDevEmail = emails.find((email: string) => !email.startsWith("dev-"));
                            return nonDevEmail || emails[0];
                          } else {
                            const devEmail = emails.find((email: string) => email.startsWith("dev-"));
                            return devEmail || emails[0];
                          }
                        })()}
                      </Snippet>
                    </div>
                  ))}
                </div>
              ) : (
                <Snippet
                  size="sm"
                  variant="flat"
                  hideSymbol
                  classNames={{
                    base: "bg-white text-vistagrey/70",
                  }}
                >
                  email
                </Snippet>
              )}
            </div>
          }
        >
          <span className="text-sm capitalize">{!env && <b>[DEV] </b>}{settingsInterface?.settings?.organization?.name}</span>
        </Tooltip>
      </div>
      <div className=" text-vistagrey/70 mx-3"> / </div>
      <div className="flex items-center text-xl text-vistagrey/70">
        <FaUserCircle className="mx-2" />
        <span className="text-sm">
          {data.user_given_name} {data.user_name}
        </span>
      </div>
      <div className=" text-vistagrey/70 mx-3"> / </div>
      <select
        data-cy="lang-select"
        value={i18n.language?.substring(0, 2)}
        onChange={(e) => changeLanguage(e.target.value)}
        className="text-xs text-vistagrey/70 bg-transparent border border-vistagrey/30 rounded px-1 py-0.5 cursor-pointer outline-none"
      >
        {[
          { code: "en", flag: "🇬🇧", label: "EN" },
          { code: "fr", flag: "🇫🇷", label: "FR" },
          { code: "de", flag: "🇩🇪", label: "DE" },
          { code: "nl", flag: "🇳🇱", label: "NL" },
          { code: "es", flag: "🇪🇸", label: "ES" },
          { code: "it", flag: "🇮🇹", label: "IT" },
          { code: "pt", flag: "🇵🇹", label: "PT" },
          { code: "pl", flag: "🇵🇱", label: "PL" },
        ].map((lang) => (
          <option key={lang.code} value={lang.code}>
            {lang.flag} {lang.label}
          </option>
        ))}
      </select>
      <button
        type="button"
        onClick={() => {
          const newAvailability = availability === "active" ? "inactive" : "active";
          setAvailability(newAvailability);
          availabilityMutation.mutate(newAvailability);
        }}
        className="ml-auto flex items-center gap-2 mr-4 cursor-pointer"
      >
        <div className={`w-12 h-7 rounded-full relative transition-colors p-1 ${
          availability === "active" ? "bg-vistagreen" : "bg-vistactared"
        }`}>
          <div className={`w-5 h-5 rounded-full bg-white shadow transition-transform ${
            availability === "active" ? "translate-x-5" : "translate-x-0"
          }`} />
        </div>
        <span className="text-xs text-vistagrey/70 w-24 text-left">
          {availability === "active" ? t("active") : t("inactive")}
        </span>
      </button>
    </>
  );
};

export default Header;
